import React, { Fragment, useEffect, useState } from 'react'
import gql from 'graphql-tag'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import withQueryParams from 'components/withQueryParams'
import withCookieAcceptance from 'components/withCookieAcceptance'
import { compose } from 'recompose'
import yup from 'yup'
import { email } from 'utils/validations'
import { Redirect } from 'react-router-dom'
import { Mutation } from 'react-apollo'
import {
  Alert,
  Grid,
  Form,
  SecondaryLink,
  spacer,
  typography,
  color,
  mediaUp
} from 'stylist'
import SSOProviderButtons from 'components/SSOProviderButtons'
import Location from 'components/Location'
import serializeErrorMessages from 'utils/serializeErrorMessages'
import { findPlatform } from 'config/platforms'
import urlForProvider from 'utils/urlForProvider'
import { getSSOForEmailInput } from 'utils/sso'

const schema = yup.object().shape({
  email,
  firstName: yup.string().required("First name can't be blank"),
  lastName: yup.string().required("Last name can't be blank"),
  phoneNumber: yup
    .string()
    .required("Phone number can't be blank")
    .test(
      'phoneNumber',
      'Phone number must be at least 7 digits',
      value => value && value.replace(/\D/g, '').length >= 7
    ),
  consent: yup
    .boolean()
    .test(
      'is-accepted',
      'You must agree to the Terms and Conditions',
      value => value
    )
})

const AlertContainer = styled.div`
  min-height: ${spacer(10)};
  margin-bottom: ${spacer(4)};

  ${mediaUp('lg')} {
    margin-left: -${spacer(8)};
    margin-right: -${spacer(8)};
  }
`

const GreyedFormGroup = styled(Form.Group)`
  ${({ consentAccepted, matchedSsoProvider }) =>
    consentAccepted &&
    matchedSsoProvider &&
    css`
      opacity: 0.5;
      filter: grayscale(100%);
    `};
`

const StyledGrid = styled(Grid)`
  height: 100%;
`

const SmallSecondaryLink = styled(SecondaryLink)`
  ${typography('body1Medium')};
`

const Disclaimer = styled.div`
  margin-top: ${spacer(5)};
`

const FinePrint = styled.div`
  color: ${color('slate')};
  margin-top: ${spacer(3)};
  ${typography('body1Medium')};
`

const RouteSignUpIndex = ({
  match: { path },
  query,
  setCookieAcceptance,
  cookiesAccepted
}) => {
  const platform = findPlatform(query.get('client_id')) || {}
  const [matchedSsoProvider, setMatchedSsoProvider] = useState(null)
  const [redirectToSsoProvider, setRedirectToSsoProvider] = useState(false)
  const [consentAccepted, setConsentAccepted] = useState(false)
  const [redirectToConfirmationUri, setRedirectToConfirmationUri] = useState(
    false
  )
  let mounted = true
  useEffect(
    () => () => {
      mounted = false
    },
    []
  )
  const getAndSetMatchedSsoProvider = value =>
    getSSOForEmailInput(value, match => mounted && setMatchedSsoProvider(match))
  const setMatchedSsoProviderIfSSOEnabled = value => {
    if (platform.ssoSignUp) {
      getAndSetMatchedSsoProvider(value)
    }
  }

  if (matchedSsoProvider && redirectToSsoProvider && consentAccepted) {
    return <Location to={urlForProvider(matchedSsoProvider.id)} replace />
  }

  if (redirectToConfirmationUri) {
    return (
      <Location
        to={platform.confirmationUri || '/login/confirmation'}
        replace
      />
    )
  }

  return query.get('client_id') && query.get('hostname') ? (
    <Mutation
      context={{ decamelizeVariables: false }}
      mutation={gql`
        mutation SignUpMutation($input: SignUpInput!) {
          signUp(input: $input) {
            user {
              id
            }
          }
        }
      `}
    >
      {signUp => (
        <StyledGrid
          gridTemplateRows="auto"
          justifyContent="center"
          alignContent="center"
        >
          <Form
            initialValues={{
              consent: false,
              email: '',
              firstName: '',
              lastName: '',
              phoneNumber: ''
            }}
            validationSchema={schema}
            onSubmit={({ consent, ...attributes }) =>
              signUp({
                variables: {
                  input: {
                    ...attributes,
                    hostname: query.get('hostname'),
                    redirectUri: query.get('redirect_uri')
                  }
                }
              })
                .then(() => {
                  setRedirectToConfirmationUri(true)
                })
                .catch(e => Promise.reject(serializeErrorMessages(e)))
            }
          >
            {matchedSsoProvider && (
              <AlertContainer>
                <Alert
                  kind="info"
                  message={
                    consentAccepted
                      ? `You will be redirected to ${
                          matchedSsoProvider.name
                        } for authentication`
                      : `Please accept privacy policy and terms and conditions to be redirected to ${
                          matchedSsoProvider.name
                        } for authentication`
                  }
                />
              </AlertContainer>
            )}
            <Form.Errors />
            <Form.Group>
              <Form.Label htmlFor="email">Email</Form.Label>
              <Form.Input
                id="email"
                type="email"
                name="email"
                onChange={e =>
                  setMatchedSsoProviderIfSSOEnabled(e.target.value)
                }
                onBlur={() =>
                  setRedirectToSsoProvider(Boolean(matchedSsoProvider))
                }
              />
            </Form.Group>
            <GreyedFormGroup
              matchedSsoProvider={matchedSsoProvider}
              consentAccepted={consentAccepted}
            >
              <Form.Label htmlFor="firstName">First Name</Form.Label>
              <Form.Input id="firstName" type="text" name="firstName" />
            </GreyedFormGroup>
            <GreyedFormGroup
              matchedSsoProvider={matchedSsoProvider}
              consentAccepted={consentAccepted}
            >
              <Form.Label htmlFor="lastName">Last Name</Form.Label>
              <Form.Input id="lastName" type="text" name="lastName" />
            </GreyedFormGroup>
            <GreyedFormGroup
              matchedSsoProvider={matchedSsoProvider}
              consentAccepted={consentAccepted}
            >
              <Form.Label htmlFor="phoneNumber">Phone Number</Form.Label>
              <Form.Input
                id="phoneNumber"
                type="tel"
                name="phoneNumber"
                formatValue={value =>
                  value.replace(/(^\+?(?:[\d()\- ]+)?)(.*)/, '$1')
                }
              />
            </GreyedFormGroup>
            <Form.Group>
              <Form.Input
                type="checkbox"
                name="consent"
                label={
                  <Fragment>
                    By signing up, you agree to our{' '}
                    <SmallSecondaryLink
                      external
                      target="_blank"
                      href="https://legal.forsta.com/legal/privacy-notice/"
                    >
                      Privacy Policy
                    </SmallSecondaryLink>{' '}
                    and our{' '}
                    <SmallSecondaryLink
                      external
                      target="_blank"
                      href="https://legal.forsta.com/legal/tos/"
                    >
                      Terms and Conditions
                    </SmallSecondaryLink>
                  </Fragment>
                }
                onChange={e => {
                  setConsentAccepted(e.target.checked)
                  setCookieAcceptance(e.target.checked)
                }}
                checked={consentAccepted}
              />
            </Form.Group>
            <Form.Group buttons>
              <Form.SubmitButton block kind="primary">
                Sign Up
              </Form.SubmitButton>
            </Form.Group>
            {platform.ssoSignUp && (
              <Form.Group>
                <SSOProviderButtons
                  cookiesAccepted={cookiesAccepted}
                  isSignUp
                />
              </Form.Group>
            )}
          </Form>
          <Disclaimer>
            Already have an account?{' '}
            <SecondaryLink to={`/login${window.location.search}`}>
              Sign in
            </SecondaryLink>
          </Disclaimer>
          <FinePrint>
            We collect this information to send you free content, offers, and
            product updates. Visit our recently updated{' '}
            <SmallSecondaryLink
              external
              target="_blank"
              href="https://legal.forsta.com/legal/privacy-notice/"
            >
              Privacy Policy
            </SmallSecondaryLink>{' '}
            for details on how we protect and manage your submitted data.
          </FinePrint>
        </StyledGrid>
      )}
    </Mutation>
  ) : (
    <Redirect to="/" />
  )
}

export default compose(
  withCookieAcceptance,
  withQueryParams
)(RouteSignUpIndex)



// WEBPACK FOOTER //
// ./src/routes/sign-up/index.js